import { useCallback, useEffect, useState } from "react";
import { useRecordContext } from "react-admin";
import { Box, Chip, CircularProgress, Grid, Typography, useMediaQuery } from "@mui/material";
import { settings } from "app";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import dataProvider from "server/dataProvider";
import { employeeRequests } from "api/employeeApi";
import { useNotifyError } from "utils/useNotifyError";
import EditApprovalSubstitutesDialog from "./EditApprovalSubstitutesDialog";
import { substituteLabel } from "./utils";
import { SectionTitle } from "features/SectionTitle";

const ApprovalSubstitutesSection = () => {
  const record = useRecordContext();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const notifyError = useNotifyError();

  const [substitutes, setSubstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const positionId = record?.id;

  const fetchSubstitutes = useCallback(async () => {
    if (!positionId) return;
    setLoading(true);
    try {
      const { data } = await dataProvider.customRequest(
        employeeRequests.getPositionApprovalSubstitutes(positionId),
        null,
        "GET",
      );
      setSubstitutes(data ?? []);
      setLoadFailed(false);
    } catch (err) {
      notifyError(err);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [positionId, notifyError]);

  useEffect(() => {
    fetchSubstitutes();
  }, [fetchSubstitutes]);

  if (!record) return null;

  return (
    <>
      <SectionTitle
        title="Заместници при одобрение"
        textAlign="center"
        sx={{ marginTop: "2rem" }}
      />
      <Grid
        container
        spacing={2}
        sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
      >
        <Grid
          item
          xs={12}
        >
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Служителите на тези длъжности могат да одобряват заявки от името на „{record.nameBg}“ в рамките на същото
            структурно звено.
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          md={9}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : substitutes.length ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {substitutes.map(({ substitutePositionId, nameBg, isActive }) => (
                <Chip
                  key={substitutePositionId}
                  label={substituteLabel(nameBg, isActive)}
                  variant={isActive ? "filled" : "outlined"}
                  disabled={!isActive}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="body2">Няма добавени заместници.</Typography>
          )}
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
          sx={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-end" }}
        >
          {/* Editing on a failed load would PUT an empty list and wipe the saved substitutes. */}
          {!loading && !loadFailed && (
            <SecuredComponent permission={permissions.canUpdateEmployeePositions}>
              <EditApprovalSubstitutesDialog
                positionId={positionId}
                positionName={record.nameBg}
                substitutes={substitutes}
                onSaved={fetchSubstitutes}
              />
            </SecuredComponent>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default ApprovalSubstitutesSection;
